import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
    DonationType,
    DonationRhythm,
    SponsorshipType,
    DonationContextEntries,
    DonationContextProperties
} from "../types";
import { DONATION_TYPE, DONATION_RHYTHM, SPONSORSHIP_TYPE } from "../utils";

export const useDonationContext = (
    donationContext: DonationContextEntries | undefined
): DonationContextProperties | undefined => {

    const { watch } = useFormContext();

    const donationType = watch(DONATION_TYPE) as DonationType | undefined;
    const donationRhythm = watch(DONATION_RHYTHM) as DonationRhythm | undefined;
    const sponsorshipType = watch(SPONSORSHIP_TYPE) as SponsorshipType | undefined;

    const currentContext = useMemo(() => {
        if (donationType === undefined || donationContext === undefined) {
            return undefined;
        }

        let newContext: DonationContextProperties | undefined;

        if (donationType === DonationType.Donation) {
            newContext = donationRhythm === DonationRhythm.Monthly
                ? donationContext?.monthly
                : donationContext?.unique;
        } else if (donationType === DonationType.Product) {
            newContext = donationContext as DonationContextProperties;
        } else if (donationType === DonationType.Sponsorship) {
            switch (sponsorshipType) {
                case SponsorshipType.ChildSponsorship:
                    newContext = donationContext?.childSponsorship;
                    break;
                case SponsorshipType.VillageSponsorshipInGermany:
                    newContext = donationContext?.villageInGermanySponsorship;
                    break;
                case SponsorshipType.VillageSponsorshipAbroad:
                    newContext = donationContext?.villageAbroadSponsorship;
                    break;
                default:
                    newContext = undefined;
            }
        }

        return newContext && Object.keys(newContext).length > 0 ? newContext : undefined;
    }, [donationType, donationRhythm, sponsorshipType, donationContext]);

    return currentContext;
};
